import { useRef, useState, useEffect, useCallback } from "react";
import { Image, Flex, Box, Spinner } from "@chakra-ui/react";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import Fullscreen from "../Fullscreen";
import ControlButton from "./ControlButton";
import Video from "./Video";
import CurrentTime from "./CurrentTime";
import Slider from "src/components/Slider";
import time from "src/utils/time";
import { BodyText2 } from "src/atoms/typography";
import Fade from "src/components/Fade";

function AbsoluteCenter({ children }) {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      position="absolute"
      width="100%"
      height="100%"
      top="0"
      left="0"
    >
      {children}
    </Flex>
  );
}

/**
 * @typedef {Object} RefType
 * @property {HTMLVideoElement} current
 */

export default function CapturedVideo({
  src,
  videoThumbnailSrc,
  autoplay = false,
  onClose,
  ratio
}) {
  /** @type {RefType} */
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const animationFrameRef = useRef(null);

  const [status, setStatus] = useState(autoplay ? "loading" : "resting");
  const [hasGenericFailure, setHasGenericFailure] = useState(false);
  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [duration, setDuration] = useState(null);
  const [currentTime, setCurrentTime] = useState(null);

  useEffect(() => {
    return () => {
      clearTimeout(retryTimeoutRef.current);
      cancelAnimationFrame(animationFrameRef.current);
    };
  }, []);

  const onTimeUpdate = useCallback(() => {
    if (animationFrameRef.current) return;
    animationFrameRef.current = requestAnimationFrame(() => {
      animationFrameRef.current = null;
      if (!videoRef.current.paused) {
        setCurrentTime(videoRef.current?.currentTime);
      }
    });
  }, []);

  async function openInFullscreen() {
    if (!videoRef.current) return;
    try {
      if (window.document.fullscreenElement) {
        await window.document.exitFullscreen();
        return;
      }
      await videoRef.current.requestFullscreen();
    } catch (err) {
      setHasGenericFailure(true);
    }
  }

  const seek = (value) => {
    if (status !== "paused") setStatus("paused");
    if (!videoRef.current.paused) videoRef.current.pause();
    setCurrentTime(value);
    videoRef.current.currentTime = value;
  };

  const seekCommit = async (value) => {
    setCurrentTime(value);
    videoRef.current.currentTime = value;
  };

  return (
    <>
      <GenericFailureDialog isOpen={hasGenericFailure} onDismiss={onClose} />
      <Flex ref={containerRef} position="relative" width="100%" height="100%">
        {(status === "loading" || status === "resting") &&
          videoThumbnailSrc && (
            <Image
              width={ratio > 1 ? "632px" : "100%"}
              height={ratio < 1 ? "90vh" : "auto"}
              src={videoThumbnailSrc}
              maxWidth="100%"
              maxHeight="90vh"
            />
          )}
        {(status === "loading" || (status === "paused" && !canPlayThrough)) && (
          <AbsoluteCenter>
            <Spinner />
          </AbsoluteCenter>
        )}
        {status !== "resting" && (
          <Box
            {...(status === "loading" && {
              position: "absolute",
              opacity: 0
            })}
          >
            <Video
              in={canPlayThrough}
              ref={videoRef}
              src={src}
              onEntered={() => {
                if (videoRef.current && status === "loading") {
                  videoRef.current.play();
                }
              }}
              onPlay={() => setStatus("playing")}
              onError={() => {
                const triedRetry = Boolean(retryTimeoutRef.current);
                if (triedRetry) {
                  setHasGenericFailure(true);
                  return;
                }
                retryTimeoutRef.current = setTimeout(() => {
                  videoRef.current.src = src;
                }, 5000);
              }}
              onEnded={() => setStatus("paused")}
              onCanPlayThrough={() => {
                setCanPlayThrough(true);
              }}
              onDurationChange={() => {
                setDuration(videoRef.current.duration);
              }}
              onTimeUpdate={onTimeUpdate}
              ratio={ratio}
            />
          </Box>
        )}

        <Box
          position="absolute"
          bottom="0"
          right="0"
          width="100%"
          height="20vh"
          backgroundImage="linear-gradient(to bottom,rgba(0, 0, 0, 0),rgba(0, 0, 0, 0.6))"
        />
        <Fade in={currentTime !== null || duration !== null || !autoplay}>
          <Flex
            pos="absolute"
            left="0"
            bottom="12px"
            w="100%"
            px="16px"
            alignItems="center"
            columnGap="12px"
          >
            <ControlButton
              status={status}
              setStatus={setStatus}
              play={() => videoRef.current.play()}
              pause={() => videoRef.current.pause()}
              containerRef={containerRef}
              duration={duration}
              currentTime={currentTime}
            />
            <CurrentTime
              status={status}
              currentTime={currentTime}
              duration={duration}
            />
            <Slider
              step={0.2}
              value={currentTime || 0}
              max={duration || 10}
              onChange={seek}
              onChangeEnd={seekCommit}
            />
            <BodyText2 color="white" textAlign="center" ml="12px">
              {time.duration(duration * 1000).format("m:ss")}
            </BodyText2>
          </Flex>
        </Fade>

        <Fullscreen status={status} onClick={openInFullscreen} />
      </Flex>
    </>
  );
}
